﻿/* https://brandplatform.smartphoto.com/general/molecules/search-bar/ */
import { createTextInputWithAutoComplete } from "../atoms/form-input";
import { expressQuerySelector } from "../../common/html";
import { isValidTextWithoutAngleBrackets } from "../../utils/validation";
import { initTooltip } from "./tooltip";

export function initSearchBar(
	desktopSearchBarContainer: HTMLElement,
	mobileSearchBarContainer: HTMLElement,
	autoCompleteSearch: (searchTerm: string) => Promise<string[]>,
	onOpen?: () => void,
	onClose?: () => void,
) {
	const initAutoComplete = (buttonEl: HTMLElement, inputEl: HTMLInputElement, clearSearchButtonEl: HTMLElement) => {
		const buttonDisabledClass = "a-action--disabled";
		const form = inputEl.parentElement! as HTMLFormElement;

		if (buttonEl) buttonEl.classList.add(buttonDisabledClass);

		const renderTextWithHighlights = (item: { value: string; label: string }, currentSearchterm: string) => {
			currentSearchterm = currentSearchterm.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
			const re = new RegExp("(" + currentSearchterm.split(" ").join("|") + ")", "gi");
			const value = item.value.replace(re, "<span class='u-color-style--blue-500'>$1</span>");
			return `<span class"a-typography a-typography--remarks">${value}</span>`;
		};

		buttonEl.onkeyup = (ev: KeyboardEvent) => {
			if (ev.key === "Enter") {
				if (inputEl && inputEl.value) {
					fixInputValue(inputEl);
					form.submit();
				} else {
					inputEl.focus();
				}
			}
		};

		clearSearchButtonEl.addEventListener("click", () => {
			inputEl.value = "";
			clearSearchButtonEl.classList.add("u-hide");
			buttonEl && buttonEl.classList.toggle(buttonDisabledClass, !inputEl.value);
		});

		inputEl.onkeyup = function (ev: KeyboardEvent) {
			clearSearchButtonEl && clearSearchButtonEl.classList.toggle("u-hide", !inputEl.value.length);
			buttonEl && buttonEl.classList.toggle(buttonDisabledClass, !inputEl.value);
			if (ev.key === "Enter") {
				if (inputEl && inputEl.value) {
					fixInputValue(inputEl);
					form.submit();
				} else {
					inputEl.focus();
				}
			}
		};

		form.onsubmit = function (e) {
			if (!inputEl || !inputEl.value || !validateInput(inputEl)) {
				e.preventDefault();
				return;
			}

			fixInputValue(inputEl);
		};
		return createTextInputWithAutoComplete(inputEl, {
			onSelect: () => {
				onClose && onClose();
				form.submit();
			},
			autoCompleteSearch: (searchTerm) => {
				if (validateInput(inputEl, searchTerm))
					return autoCompleteSearch(searchTerm).then((res) => res.map((item) => ({ value: item, label: item })));

				return;
			},
			onOpen: onOpen,
			onClose: onClose,
			renderTextContent: renderTextWithHighlights,
			containerClass: "m-suggestion-list m-search-bar__suggestion-list technical-autocomplete-suggestions",
			containerInsertPosition: "afterend",
		});
	};

	const fixInputValue = (inputEl: HTMLInputElement) => {
		inputEl.value = inputEl.value.replace(/[<>]+/g, "");
	};

	const validateInput = (inputEl: HTMLInputElement, searchTerm?: string) => {
		return isValidTextWithoutAngleBrackets(searchTerm ? searchTerm : inputEl.value);
	};

	const initMobileSearchBar = (containerEl: HTMLElement) => {
		const inputEl = expressQuerySelector<HTMLInputElement>(containerEl, ".m-search-bar__input", true);
		const buttonEl = expressQuerySelector<HTMLInputElement>(containerEl, ".technical-search", true);
		const clearSearchButtonEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-clear-search", true);
		const autoComplete = initAutoComplete(buttonEl, inputEl, clearSearchButtonEl);
		const onSearchTooltipOpen = () => inputEl.focus();
		const form = inputEl.parentElement! as HTMLFormElement;

		form.onsubmit = function (e) {
			if (!inputEl || !inputEl.value || !validateInput(inputEl)) {
				e.preventDefault();
				return;
			}

			fixInputValue(inputEl);
		};

		const overlayComponent = initTooltip({
			buttonEl: containerEl.parentElement.previousElementSibling as HTMLElement,
			containerEl: containerEl.parentElement,
			onOpen: onSearchTooltipOpen,
			openClass: "menu-is-open",
			tooltipSelector: ".technical-mobile-searchbar",
			toggleIconSelector: ".icon-search_thin",
		});

		return {
			dipose: () => {
				overlayComponent.dispose();
				autoComplete.dispose();
			},
		};
	};

	// init desktop search autocomplete
	const desktopSearchBar = initAutoComplete(
		expressQuerySelector<HTMLInputElement>(desktopSearchBarContainer, ".technical-search", true),
		expressQuerySelector<HTMLInputElement>(desktopSearchBarContainer, ".m-search-bar__input", true),
		expressQuerySelector<HTMLElement>(desktopSearchBarContainer, ".technical-clear-search", true),
	);

	// init mobile search
	const mobileSearchBar = initMobileSearchBar(mobileSearchBarContainer);

	return {
		dispose: () => {
			mobileSearchBar.dipose();
			desktopSearchBar.dispose();
		},
	};
}
